import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Chip,
  FormControl,
  InputLabel,
  Select,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
} from '@mui/material';
import {
  Search,
  Add,
  MoreVert,
  Delete,
  Security,
  ContentCopy,
  Edit,
  Lock,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useRoles, useDeleteRole, useCloneRole } from '../../hooks/useRoles';
import { useOrganizations } from '../../hooks/useFleet';
import RoleCreateDialog from '../../components/RoleCreateDialog';
import type { Role } from '../../api/types';
import { SectionTabs } from '../../components/SectionTabs';
import { MANAGEMENT_TABS, MANAGEMENT_ACCENT_COLOR } from './managementTabs';
import { managementHelp } from '../../components/PageHelp/helpContent';

const scopeColors: Record<string, string> = {
  global: '#ef4444',
  organization: '#6366f1',
  resource: '#10b981',
};

const scopeLabels: Record<string, string> = {
  global: 'Global',
  organization: 'Organization',
  resource: 'Resource',
};

const RolesPage: React.FC = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [searchQuery, setSearchQuery] = useState('');
  const [scopeFilter, setScopeFilter] = useState<string>('');
  const [orgFilter, setOrgFilter] = useState<string>('');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [cloneDialogOpen, setCloneDialogOpen] = useState(false);
  const [cloneName, setCloneName] = useState('');
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });

  const { data, isLoading, error, refetch } = useRoles({
    page: page + 1,
    size: rowsPerPage,
    search: searchQuery || undefined,
    scope: scopeFilter || undefined,
    organization_id: orgFilter || undefined,
    include_system: true,
  });

  const { data: orgsData } = useOrganizations({ size: 100 });
  const deleteMutation = useDeleteRole();
  const cloneMutation = useCloneRole();

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, role: Role) => {
    setAnchorEl(event.currentTarget);
    setSelectedRole(role);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRole(null);
  };

  const handleEditClick = () => {
    if (selectedRole) {
      navigate(`/management/roles/${selectedRole.id}`);
    }
    handleMenuClose();
  };

  const handleDeleteClick = () => {
    setDeleteConfirmOpen(true);
    setAnchorEl(null);
  };

  const handleCloneClick = () => {
    if (selectedRole) {
      setCloneName(`${selectedRole.name} (Copy)`);
      setCloneDialogOpen(true);
    }
    setAnchorEl(null);
  };

  const handleDeleteConfirm = async () => {
    if (selectedRole) {
      try {
        await deleteMutation.mutateAsync(selectedRole.id);
        setSnackbar({ open: true, message: 'Role deleted successfully', severity: 'success' });
      } catch (err: any) {
        setSnackbar({ open: true, message: err?.response?.data?.error || 'Failed to delete role', severity: 'error' });
      }
    }
    setDeleteConfirmOpen(false);
    setSelectedRole(null);
  };

  const handleCloneConfirm = async () => {
    if (selectedRole && cloneName.trim()) {
      try {
        await cloneMutation.mutateAsync({
          id: selectedRole.id,
          data: { name: cloneName.trim() },
        });
        setSnackbar({ open: true, message: 'Role cloned successfully', severity: 'success' });
      } catch (err: any) {
        setSnackbar({ open: true, message: err?.response?.data?.error || 'Failed to clone role', severity: 'error' });
      }
    }
    setCloneDialogOpen(false);
    setCloneName('');
    setSelectedRole(null);
  };

  const handleCreateSuccess = () => {
    setSnackbar({ open: true, message: 'Role created successfully', severity: 'success' });
    setCreateDialogOpen(false);
    refetch();
  };

  const roles = data?.data || [];
  const totalCount = data?.total || 0;

  return (
    <Box sx={{ p: 3 }}>
      <SectionTabs
        title="Management"
        tabs={MANAGEMENT_TABS}
        accentColor={MANAGEMENT_ACCENT_COLOR}
        help={managementHelp.roles}
        actionButton={{
          label: 'New Role',
          icon: <Add />,
          onClick: () => setCreateDialogOpen(true),
        }}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Failed to load roles. Please try again.
        </Alert>
      )}

      <Paper sx={{ bgcolor: '#1e1e1e', borderRadius: 2 }}>
        <Box sx={{ p: 2, borderBottom: '1px solid #333', display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            placeholder="Search roles..."
            size="small"
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setPage(0); }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
            sx={{ width: 250 }}
          />

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Scope</InputLabel>
            <Select
              value={scopeFilter}
              label="Scope"
              onChange={(e) => { setScopeFilter(e.target.value); setPage(0); }}
            >
              <MenuItem value="">All Scopes</MenuItem>
              <MenuItem value="global">Global</MenuItem>
              <MenuItem value="organization">Organization</MenuItem>
              <MenuItem value="resource">Resource</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Organization</InputLabel>
            <Select
              value={orgFilter}
              label="Organization"
              onChange={(e) => { setOrgFilter(e.target.value); setPage(0); }}
            >
              <MenuItem value="">All Organizations</MenuItem>
              {(orgsData?.data || []).map((org) => (
                <MenuItem key={org.id} value={org.id}>{org.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress sx={{ color: '#6366f1' }} />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Role</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Scope</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Organization</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Priority</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Permissions</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Users</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {roles.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                        <Security sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                        <Typography color="text.secondary">
                          No roles found. Create your first custom role to get started.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    roles.map((role) => (
                      <TableRow key={role.id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/management/roles/${role.id}`)}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {role.is_system ? (
                              <Lock sx={{ color: '#f59e0b', fontSize: 20 }} />
                            ) : (
                              <Security sx={{ color: '#6366f1' }} />
                            )}
                            <Box>
                              <Typography fontWeight={500}>{role.name}</Typography>
                              {role.description && (
                                <Typography variant="caption" color="text.secondary">
                                  {role.description}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={scopeLabels[role.scope] || role.scope}
                            size="small"
                            sx={{
                              bgcolor: scopeColors[role.scope] || '#666',
                              color: 'white',
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          {role.organization_name || (
                            <Typography variant="body2" color="text.secondary">
                              {role.scope === 'global' ? 'All Organizations' : '-'}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={role.priority}
                            size="small"
                            variant="outlined"
                            sx={{ minWidth: 50 }}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={role.permission_count || 0}
                            size="small"
                            variant="outlined"
                            color="primary"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={role.user_count || 0}
                            size="small"
                            variant="outlined"
                            color="secondary"
                          />
                        </TableCell>
                        <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                          <IconButton size="small" onClick={(e) => handleMenuOpen(e, role)}>
                            <MoreVert />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          </>
        )}
      </Paper>

      {/* Actions Menu */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={handleEditClick}>
          <ListItemIcon>
            <Edit fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit Role</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleCloneClick}>
          <ListItemIcon>
            <ContentCopy fontSize="small" />
          </ListItemIcon>
          <ListItemText>Clone Role</ListItemText>
        </MenuItem>
        {!selectedRole?.is_system && (
          <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }}>
            <ListItemIcon>
              <Delete fontSize="small" sx={{ color: 'error.main' }} />
            </ListItemIcon>
            <ListItemText>Delete</ListItemText>
          </MenuItem>
        )}
      </Menu>

      {/* Create Role Dialog */}
      <RoleCreateDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSuccess={handleCreateSuccess}
      />

      {/* Clone Dialog */}
      <Dialog open={cloneDialogOpen} onClose={() => setCloneDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Clone Role</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Create a copy of "{selectedRole?.name}" with all its permissions.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label="New Role Name"
            value={cloneName}
            onChange={(e) => setCloneName(e.target.value)}
            placeholder="Enter name for the cloned role"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCloneDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleCloneConfirm}
            variant="contained"
            disabled={!cloneName.trim() || cloneMutation.isPending}
            sx={{ bgcolor: '#6366f1', '&:hover': { bgcolor: '#4f46e5' } }}
          >
            {cloneMutation.isPending ? <CircularProgress size={20} /> : 'Clone'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Delete Role?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{selectedRole?.name}</strong>?
            This action cannot be undone.
          </Typography>
          {(selectedRole?.user_count || 0) > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              This role is assigned to {selectedRole?.user_count} user(s). They will lose these permissions.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RolesPage;
